import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    CCard,
    CCardHeader,
    CCardBody,
    CButton,
    CBadge,
} from '@coreui/react';
import { CSmartTable } from '@coreui/react-pro';

import DatePicker from "react-datepicker";
import CIcon from '@coreui/icons-react';
import { cilMagnifyingGlass } from '@coreui/icons';

import { ADMIN_PERSON_IDENTIFIERS_BY_ACCOUNT, COMPONENT_NAME, DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT } from '../../../../../../constants';
import { callTokenApi } from '../../../../../../apiCaller';
import { formatDate, formatDateTimeZone, toastError } from '../../../../../../utils';
import { useDebounce } from '../../../../../../helpers/customHooks'
import { useDispatch } from 'react-redux';
import { setFlexibleModal } from '../../../../../../actions/admin';
import { getEnrichmentTypeColor } from '../../../centralized-person';

const EmptyState = () => {
    return (
        <div className="d-flex flex-column align-items-center justify-content-center p-4 text-muted">
            <div className="bg-light rounded-circle p-3 mb-3">
                <CIcon icon={cilMagnifyingGlass} size="3xl" className="text-secondary" />
            </div>
            <h6 className="fw-bold text-dark">No persons found</h6>
            <p className="mb-0 small text-center" style={{ maxWidth: '300px' }}>
                We couldn't find any centralized persons matching your filters. 
                Try adjusting your search criteria.
            </p>
        </div>
    );
};

const PersonIdentifiersPanel = ({ account, personId, title, hiddenCard = false, showLv2 }) => {
    const { id: accountId, timeZone } = account

    const dispatch = useDispatch();
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rawColumnFilter, setRawColumnFilter] = useState({});
    const debouncedColumnFilter = useDebounce(rawColumnFilter, 500);

    const [activePage, setActivePage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const columns = useMemo(() => [
        { key: 'id', label: 'ID', _props: { scope: 'col' } },
        { key: 'personId', label: 'Person ID', _props: { scope: 'col' } },
        { key: 'value', label: 'Value', _props: { scope: 'col' } },
        { key: 'type', label: 'Type', _props: { scope: 'col' } },
        { key: 'sourcePath', label: 'Source Path', _props: { scope: 'col' }, sorter: false },
        { key: 'validStatus', label: 'Valid Status', _props: { scope: 'col' }, sorter: false },
        { key: 'enrichType', label: 'Enrich Type', _props: { scope: 'col' } },
        { key: 'personType', label: 'Person Type', _props: { scope: 'col' } },
        { key: 'validAt', label: 'Valid At', _props: { scope: 'col' }, filter: (_, onChange) => <DatePicker
            dateFormat="yyyy-MM-dd"
            className="form-control form-control-sm"
            selected={rawColumnFilter.validAt ? new Date(rawColumnFilter.validAt) : null}
            onChange={onChange}
        /> },
        { key: 'companyId', label: 'CompanyId', _props: { scope: 'col' } },
        { key: 'created_at', label: 'Created Date', _props: { scope: 'col' }, filter: (_, onChange) => <DatePicker
            dateFormat="yyyy-MM-dd"
            className="form-control form-control-sm"
            selected={rawColumnFilter.created_at ? new Date(rawColumnFilter.created_at) : null}
            onChange={onChange}
        /> },
        {
            key: 'actions',
            label: 'Actions',
            _props: { scope: 'col', className: 'text-center' },
            filter: false,
            sorter: false,
        }
    ], [rawColumnFilter]);

    const fetchPersons = useCallback(async (currentPage, currentPageSize, currentColumnFilter) => {
        if (!accountId) {
            setItems([]);
            setTotalPages(0);
            setError("Account ID is required to fetch persons.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('pageSize', currentPageSize);
            if (personId) params.append('personId', personId);

            Object.keys(currentColumnFilter).forEach((key) => {
                let value = currentColumnFilter[key];

                if (value !== undefined && value !== '' && value !== null) {
                    if (['created_at', 'validAt'].includes(key) && value instanceof Date) {
                        value = formatDate(value, DEFAULT_DATE_FORMAT);
                    }

                    params.append(key, value);
                }
            });

            const apiUrl = `${ADMIN_PERSON_IDENTIFIERS_BY_ACCOUNT.replace(':accountId', accountId)}?${params.toString()}`;

            const response = await callTokenApi(apiUrl, 'GET', null);

            if (response.status === 200 && response.data?.success) {
                setItems(response.data?.data || []);
                setTotalPages(response.data?.pagination?.totalPages || 0);
            } else {
                const errorMsg = response.data?.message || `Failed to fetch data (status: ${response.status})`;
                toastError({ data: { message: errorMsg } });
                setError(errorMsg);
                setItems([]);
                setTotalPages(0);
            }
        } catch (err) {
            console.error("API call failed:", err);
            const errorMessage = err.message || 'An unexpected error occurred.';
            toastError({ data: { message: errorMessage } });
            setError(errorMessage);
            setItems([]);
            setTotalPages(0);
        } finally {
            setIsLoading(false);
        }
    }, [accountId, personId]);

    useEffect(() => {
        fetchPersons(activePage, pageSize, debouncedColumnFilter);
    }, [fetchPersons, activePage, pageSize, debouncedColumnFilter]);

    const handleRetrieve = useCallback((id) => {
        if (showLv2) {
            dispatch(
                setFlexibleModal({
                    show: true,
                    showLv2: true,
                    componentLv2: COMPONENT_NAME.ADMIN_PERSON_IDENTIFIER,
                    component: COMPONENT_NAME.ADMIN_CENTRALIZED_PERSON,
                    ruleIdLv2: id,
                    timeZone
                })
            );
        } else {
            dispatch(
                setFlexibleModal({
                    show: true,
                    component: COMPONENT_NAME.ADMIN_PERSON_IDENTIFIER,
                    attributes: {
                        id,
                        timeZone
                    }
                })
            );
        }
    }, [showLv2, dispatch]); // eslint-disable-line 

    const scopedColumns = useMemo(() => ({
        id: (item) => (
            <td className='text-nowrap'>{item.id}</td>
        ),
        created_at: (item) => (
            <td>{formatDateTimeZone(item.created_at, DEFAULT_DATE_TIME_FORMAT, timeZone)}</td>
        ),
        personId: (item) => (
            <td className='text-nowrap'>{item.personId}</td>
        ),
        value: (item) => (
            <td>{item.value || '--'}</td>
        ),
        type: (item) => (
            <td>{item.type || '--'}</td>
        ),
        validStatus: (item) => (
            <td>
                {item.validStatus ? (
                    <CBadge color={item.validStatus.toLowerCase() === 'valid' ? 'success' : 'danger'} className="text-white">
                        {item.validStatus}
                    </CBadge>
                ) : '--'}
            </td>
        ),
        enrichType: (item) => (
            <td>
                {item.enrichType ? (
                    <CBadge color={getEnrichmentTypeColor(item.enrichType)} className="text-white">
                        {item.enrichType}
                    </CBadge>
                ) : '--'}
            </td>
        ),
        personType: (item) => (
            <td>
                {item.personType ? (
                    <CBadge color="secondary" className="text-white">
                        {item.personType}
                    </CBadge>
                ) : '--'}
            </td>
        ),
        validAt: (item) => (
            <td className='text-nowrap'>{item.validAt ? formatDateTimeZone(item.validAt, DEFAULT_DATE_TIME_FORMAT, timeZone) : '--'}</td>
        ),
        actions: (item) => (
            <td className="text-center">
                <CButton
                    color="info"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRetrieve(item.id)}
                >
                    View
                </CButton>
            </td>
        ),
    }), [handleRetrieve]); // eslint-disable-line 

    const handlePageChange = (newPage) => {
        setActivePage(newPage);
    };

    const handleItemsPerPageChange = (newPageSize) => {
        setPageSize(newPageSize);
        setActivePage(1);
    };

    const handleColumnFilterChange = (newFilter) => {
        setRawColumnFilter(newFilter);
        setActivePage(1);
    }

    const tableComponent = (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            <div style={{ overflowX: 'auto', width: '100%' }}>
                <div style={{ minWidth: 'max-content' }}>
                    <CSmartTable
                        loading={isLoading}
                        noItemsLabel={isLoading ? null : <EmptyState />}
                        items={items}
                        columns={columns}
                        scopedColumns={scopedColumns}
                        pagination={{
                            external: true,
                        }}
                        paginationProps={{
                            activePage: activePage,
                            pages: totalPages,
                        }}
                        onActivePageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        onColumnFilterChange={handleColumnFilterChange}
                        itemsPerPage={pageSize}
                        itemsPerPageSelect
                        columnFilter={{ external: true }}
                        columnSorter
                        tableProps={{
                            hover: true,
                            responsive: true,
                            className: 'table-striped align-middle'
                        }}
                    />
                </div>
            </div>
        </>
    );

    return (
        <>
            {hiddenCard ? (
                tableComponent
            ) : (
                <CCard>
                    {title && (
                        <CCardHeader>
                            <h5 className='mb-0'>{title}</h5>
                        </CCardHeader>
                    )}
                    <CCardBody>
                        {tableComponent}
                    </CCardBody>
                </CCard>
            )}
        </>
    );
};

export default PersonIdentifiersPanel;